import React, { useEffect } from 'react';
import { useConfigurator } from '../../context/ConfiguratorContext';
import { getAssetPath } from '../../utils/assetPath';

/**
 * Component that manages dynamic Open Graph meta tags based on the selected configurator
 */
const MetaTags: React.FC = () => {
  const { selectedConfiguratorId, availableConfigurators, currentConfig } = useConfigurator();

  useEffect(() => {
    if (!selectedConfiguratorId || availableConfigurators.length === 0) return;

    const selectedConfigurator = availableConfigurators.find(
      config => config.id === selectedConfiguratorId
    );

    if (!selectedConfigurator) return;

    // Update meta tags based on the selected configurator
    updateMetaTags(selectedConfigurator.name, selectedConfigurator.id);
  }, [selectedConfiguratorId, availableConfigurators, currentConfig]);

  const updateMetaTags = (title: string, id: string) => {
    // Get the base URL
    const baseUrl = window.location.origin;
    
    // Update title
    document.title = `${title} - Estetica Home Configurator`;
    
    // Update description meta tag
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      descriptionMeta.setAttribute('content', `Configure your ${title} from Estetica Home in 3D`);
    }
    
    // Update OG meta tags
    updateOrCreateMetaTag('og:title', `${title} - Estetica Home 3D Configurator`);
    updateOrCreateMetaTag('og:description', `Customize and configure your ${title} in 3D`);
    updateOrCreateMetaTag('og:type', 'website');
    updateOrCreateMetaTag('og:url', `${baseUrl}?product=${id}`);
    
    // Set OG image to the product image
    // Use the product image directly from the assets folder
    // Ensure we account for the PUBLIC_URL environment variable
    const publicUrl = process.env.PUBLIC_URL || '';
    const imageUrl = `${baseUrl}${publicUrl}${getAssetPath(`/images/products-jpg/${id}.jpg`).replace(/^\//, '')}`;
    updateOrCreateMetaTag('og:image', imageUrl);
    updateOrCreateMetaTag('og:logo', imageUrl);
    
    // Additional meta tags for better sharing
    updateOrCreateMetaTag('og:site_name', 'Estetica Home Configurator');
    updateOrCreateMetaTag('twitter:card', 'summary_large_image');
    updateOrCreateMetaTag('twitter:title', `${title} - Estetica Home 3D Configurator`);
    updateOrCreateMetaTag('twitter:description', `Customize and configure your ${title} in 3D`);
    updateOrCreateMetaTag('twitter:image', imageUrl);
  };

  const updateOrCreateMetaTag = (property: string, content: string) => {
    // Check if meta tag exists
    let metaTag = document.querySelector(`meta[property="${property}"]`);
    
    // If it doesn't exist, create it
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute('property', property);
      document.head.appendChild(metaTag);
    }
    
    // Set the content
    metaTag.setAttribute('content', content);
  };

  // This component doesn't render anything visible
  return null;
};

export default MetaTags;
